import { DropdownProps } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import { classNames } from 'primereact/utils';
import { ChangeEvent, useEffect, useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import ErrorMessage from '../../../../../../../../../../../../../../components/error-message';
import { useListWasteTreatments } from '../../../../../../../../../../../../../../hooks/api/waste-treatments';
import { IWasteTreatment } from '../../../../../../PcfWizard.type';
import { filterWasteTreatments } from './helper/helper';
import Menu from './Menu';
import styles from './WasteTreatmentDropdown.module.css';

interface WasteTreatmentDropdownProps extends DropdownProps {
  name: string;
  processIndex: number;
  label?: string;
}

const WasteTreatmentDropDown = (props: WasteTreatmentDropdownProps) => {
  const { name, label, processIndex } = props;
  const { control } = useFormContext();
  const processId = useWatch({
    name: `processes[${processIndex}].process_id`,
    control,
  });

  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>('');

  const { data: wasteTreatments, isLoading } = useListWasteTreatments({
    enabled: true,
  });

  const [filteredWasteTreatments, setFilteredWasteTreatments] = useState<IWasteTreatment[]>(
    wasteTreatments || [],
  );

  // Once waste treatments are loaded, filter them by allowed processes against process_id field
  useEffect(() => {
    const _filteredWasteTreatments = filterWasteTreatments(wasteTreatments, processId);

    setFilteredWasteTreatments(_filteredWasteTreatments);
  }, [wasteTreatments, processId]);

  const {
    field: { value: inputValue, ref, onChange },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
  });

  const onInputBlur = (): void => {
    setIsMenuVisible(false);
  };

  const onInputMouseDown = (): void => {
    setIsMenuVisible(true);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    // Close the menu if "Esc" is pressed
    if (e.key === 'Escape') {
      setIsMenuVisible(false);
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;

    if (!isMenuVisible) setIsMenuVisible(true);

    setSearchString(value);

    if (value !== inputValue?.name) {
      // Unselect the value if user types something in the input right after selecting an option
      onChange(null);
    }

    // Filter options based on search string
    setFilteredWasteTreatments(() => {
      let _filteredWasteTreatments = filterWasteTreatments(wasteTreatments, processId);

      if (!value) {
        // If the search string is empty, return the initial menu back
        return _filteredWasteTreatments;
      }

      // Filter by search string
      _filteredWasteTreatments = _filteredWasteTreatments?.filter((wasteTreatment) => {
        const name = wasteTreatment?.name;
        const year = wasteTreatment?.referencePeriod;

        // Combine emission name, and referencePeriod for search
        const searchSource = `${name} | ${year?.toString()}`.toLowerCase();

        return searchSource.includes(value?.trim()?.toLowerCase());
      });

      return _filteredWasteTreatments || [];
    });
  };

  // Function to what to show on the dropdown. Considers the selected item and/or search string
  const determineDropdownValue = (): string | undefined => {
    if (inputValue?.name) {
      return inputValue.name;
    }

    if (searchString) {
      return searchString;
    }

    return '';
  };

  const onSearchMouseDown = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
  };

  const onTriggerMouseDown = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setIsMenuVisible(!isMenuVisible);
  };

  const onItemMouseDown = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
  };

  const onItemSelect = (item: Partial<IWasteTreatment>): void => {
    onChange(item);
    setSearchString(item?.name || '');
    setIsMenuVisible(false);
  };

  if (isLoading) {
    return <Skeleton className='w-full' height='45px' />;
  }

  return (
    <div>
      <div className='p-fluid mb-1' onBlur={onInputBlur}>
        <div
          className={classNames(
            'flex flex-row align-items-center p-float-label',
            styles['input-wrapper'],
          )}
          onMouseDown={onInputMouseDown}
          onKeyDown={onKeyDown}
          ref={ref}
        >
          <InputText
            ref={ref}
            name={name}
            type='text'
            onChange={onInputChange}
            value={determineDropdownValue()}
            style={{ textIndent: '2rem' }}
            className={classNames('line-height-1 text-overflow-ellipsis w-full', {
              'p-invalid': error,
            })}
          />
          <label htmlFor={name}>{label ?? ''}</label>

          {/* Search Icon */}
          <div
            className='flex align-items-center px-2 absolute left-0 mx-3 text-gray-400 z-1'
            onMouseDown={onSearchMouseDown}
          >
            <i className='pi pi-search' />
          </div>

          {/* Trigger Icon */}
          <div
            className='flex align-items-center  absolute right-0 px-2 py-2 mr-3 text-gray-400 z-1'
            onMouseDown={onTriggerMouseDown}
          >
            <i className={classNames('pi', isMenuVisible ? 'pi-angle-up' : 'pi-angle-down')} />
          </div>
        </div>
        {/* Dropdown Menu */}
        {isMenuVisible && (
          <Menu
            filteredWasteTreatments={filteredWasteTreatments}
            onItemMouseDown={onItemMouseDown}
            onItemSelect={onItemSelect}
          />
        )}
      </div>
      {invalid && <ErrorMessage message={error?.message ?? ''} />}
    </div>
  );
};

export default WasteTreatmentDropDown;
